import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {
    LaunchBrandPipelineClient,
    LaunchAuthorizedUser,
    LaunchBrandPack,
    LaunchPersona,
    LaunchResult,
} from '../../services/launch-brand-pipeline.client';

/**
 * Brand-pack collection modal for the Outbound: Launch Brand Pipeline spine.
 *
 * PROVIDE-ONLY (v1): collects the brand pack (company, email accounts, schedule,
 * authorized users, personas, and a pasted markdown copy doc per persona) plus a
 * DryRun toggle. Submits through {@link LaunchBrandPipelineClient}.
 *
 * SAFETY: there is NO start/unpause/go-live affordance anywhere in this modal,
 * and NO "generate copy" affordance — the operator pastes copy. The spine stages
 * sequences PAUSED-ready; a human flips them live in the Reply dashboard. The
 * only knob is DryRun (plan vs. write); DryRun defaults ON so the first click
 * previews the plan with zero writes.
 */
@Component({
    standalone: false,
    selector: 'obd-launch-brand-pipeline-modal',
    template: `
        <div class="lbp-backdrop" *ngIf="visible" (click)="onCancel()">
            <div class="lbp-modal" (click)="$event.stopPropagation()">
                <header class="lbp-header">
                    <h2>Launch Outbound Brand</h2>
                    <button type="button" class="lbp-close" (click)="onCancel()" aria-label="Close">×</button>
                </header>

                <section class="lbp-body">
                    <label class="lbp-field">
                        <span>Company ID (UUID)</span>
                        <input type="text" [(ngModel)]="companyId" placeholder="AAAAAAAA-…" />
                    </label>

                    <label class="lbp-field">
                        <span>Brand slug (optional — pins utm_source)</span>
                        <input type="text" [(ngModel)]="brandSlug" placeholder="acme" />
                    </label>

                    <label class="lbp-field">
                        <span>Email account IDs (comma-separated, from the shared Reply account)</span>
                        <input type="text" [(ngModel)]="emailAccountIdsCsv" placeholder="884392, 884393" />
                    </label>

                    <label class="lbp-field">
                        <span>Default schedule ID</span>
                        <input type="number" [(ngModel)]="defaultScheduleId" placeholder="548312" />
                    </label>

                    <label class="lbp-field">
                        <span>Authorized users (email:role per line; ≥1 Admin)</span>
                        <textarea rows="2" [(ngModel)]="authorizedUsersText" placeholder="admin@acme.com:Admin"></textarea>
                    </label>

                    <label class="lbp-field">
                        <span>Personas (name | criteria | apolloListName per line)</span>
                        <textarea rows="3" [(ngModel)]="personasText"
                                  placeholder="Exec | C-suite at associations | 5000 - Qualified Execs"></textarea>
                    </label>

                    <div class="lbp-field">
                        <span>Copy docs (one markdown block per persona, separated by a line "===")</span>
                        <textarea rows="6" [(ngModel)]="copyDocsText"
                                  placeholder="---\nsequence_name: Acme Exec\n---\n## Step 1\n…\n===\n…next persona…"></textarea>
                    </div>

                    <label class="lbp-toggle lbp-dryrun">
                        <input type="checkbox" [(ngModel)]="dryRun" />
                        <span><strong>DryRun</strong> — plan only, zero writes (recommended first)</span>
                    </label>

                    <p class="lbp-note">
                        Sequences are always created <strong>PAUSED</strong>. Flip them live in the Reply dashboard
                        when you’re ready. This launcher cannot start or send a sequence.
                    </p>

                    <p class="lbp-error" *ngIf="errorMessage">{{ errorMessage }}</p>
                </section>

                <footer class="lbp-footer">
                    <button type="button" class="lbp-btn-secondary" (click)="onCancel()" [disabled]="busy">Cancel</button>
                    <button type="button" class="lbp-btn-primary" (click)="onSubmit()" [disabled]="busy">
                        {{ busy ? 'Working…' : (dryRun ? 'Plan (DryRun)' : 'Launch (paused)') }}
                    </button>
                </footer>

                <section class="lbp-result" *ngIf="result">
                    <h3>{{ result.wasDryRun ? 'Plan' : 'Launched' }}</h3>
                    <p>{{ result.message }}</p>
                    <ol class="lbp-plan">
                        <li *ngFor="let s of result.plan" [class.lbp-step-error]="s.status === 'error'">
                            <span class="lbp-step-status">{{ s.status }}</span>
                            <span class="lbp-step-name">{{ s.step }}</span>
                            <span class="lbp-step-detail">{{ s.detail }}</span>
                        </li>
                    </ol>
                </section>
            </div>
        </div>
    `,
    styles: [
        `.lbp-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
         .lbp-modal { background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); font-family: var(--mj-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; width: min(640px, 92vw); max-height: 88vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.18); }
         .lbp-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
         .lbp-header h2 { margin: 0; font-size: 18px; color: var(--mj-text-primary, #1a1a1a); }
         .lbp-close { border: 0; background: none; font-size: 24px; line-height: 1; cursor: pointer; color: var(--mj-text-secondary, #71717a); }
         .lbp-close:hover { color: var(--mj-text-primary, #1a1a1a); }
         .lbp-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
         .lbp-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); }
         .lbp-field > span { color: var(--mj-text-secondary, #71717a); }
         .lbp-field input, .lbp-field textarea { font: inherit; padding: 6px 8px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-page, #fafafa); color: var(--mj-text-primary, #1a1a1a); }
         .lbp-field input:focus, .lbp-field textarea:focus { outline: none; border-color: var(--mj-brand-primary, #6366f1); }
         .lbp-field textarea { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; resize: vertical; }
         .lbp-toggle { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); }
         .lbp-dryrun { padding: 8px; border-radius: 6px; background: color-mix(in srgb, var(--mj-info, #3b82f6) 12%, var(--mj-bg-surface, #fff)); border: 1px solid color-mix(in srgb, var(--mj-info, #3b82f6) 30%, var(--mj-bg-surface, #fff)); }
         .lbp-note { font-size: 12px; color: var(--mj-text-secondary, #71717a); border-radius: 6px; padding: 8px; background: color-mix(in srgb, var(--mj-warning, #d97706) 12%, var(--mj-bg-surface, #fff)); border: 1px solid color-mix(in srgb, var(--mj-warning, #d97706) 30%, var(--mj-bg-surface, #fff)); }
         .lbp-note strong { color: var(--mj-text-primary, #1a1a1a); }
         .lbp-error { color: var(--mj-danger, #ef4444); font-size: 13px; }
         .lbp-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }
         .lbp-btn-primary { background: var(--mj-brand-primary, #6366f1); color: #fff; border: 0; border-radius: 6px; padding: 8px 16px; font: inherit; font-weight: 500; cursor: pointer; }
         .lbp-btn-primary:hover:not(:disabled) { background: color-mix(in srgb, var(--mj-brand-primary, #6366f1) 85%, #000); }
         .lbp-btn-secondary { background: transparent; border: 1px solid var(--mj-border-default, #e0e0e0); color: var(--mj-text-primary, #1a1a1a); border-radius: 6px; padding: 8px 16px; font: inherit; font-weight: 500; cursor: pointer; }
         .lbp-btn-secondary:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); }
         .lbp-btn-primary:disabled, .lbp-btn-secondary:disabled { opacity: 0.5; cursor: default; }
         .lbp-result { padding: 0 20px 20px; color: var(--mj-text-primary, #1a1a1a); }
         .lbp-result h3 { color: var(--mj-text-primary, #1a1a1a); }
         .lbp-plan { font-size: 12px; color: var(--mj-text-primary, #1a1a1a); }
         .lbp-step-status { display: inline-block; min-width: 64px; font-weight: 600; }
         .lbp-step-error .lbp-step-status { color: var(--mj-danger, #ef4444); }
         .lbp-step-detail { color: var(--mj-text-secondary, #71717a); margin-left: 8px; }`,
    ],
})
export class LaunchBrandPipelineModalComponent {
    @Input() visible = false;
    /** Prefill the company id from the dashboard's current company context. */
    @Input() set companyContext(value: string | null) {
        if (value && !this.companyId) this.companyId = value;
    }
    @Output() closed = new EventEmitter<void>();
    /** Emitted after a successful LIVE launch so the dashboard can refresh. */
    @Output() launched = new EventEmitter<LaunchResult>();

    private readonly client = inject(LaunchBrandPipelineClient);

    companyId = '';
    brandSlug = '';
    emailAccountIdsCsv = '';
    defaultScheduleId: number | null = null;
    authorizedUsersText = '';
    personasText = '';
    copyDocsText = '';
    dryRun = true;

    busy = false;
    errorMessage = '';
    result: LaunchResult | null = null;

    onCancel(): void {
        if (this.busy) return;
        this.closed.emit();
    }

    async onSubmit(): Promise<void> {
        this.errorMessage = '';
        this.result = null;

        const built = this.buildBrandPack();
        if (built.error !== null) {
            this.errorMessage = built.error;
            return;
        }
        const pack = built.pack;

        this.busy = true;
        try {
            // PROVIDE-ONLY: copy is always pasted by the operator (no generate
            // branch). The spine stages sequences PAUSED-ready — no enroll/people
            // is passed from this launcher, so no real email can be sent.
            const outcome = await this.client.launch(pack, this.dryRun);
            if (outcome.kind !== 'success') {
                this.errorMessage = outcome.message;
                return;
            }
            this.result = outcome.data;
            if (!outcome.data.wasDryRun) {
                this.launched.emit(outcome.data);
            }
        } finally {
            this.busy = false;
        }
    }

    /** Parse the form fields into a LaunchBrandPack, or return a flat error. */
    private buildBrandPack(): { pack: LaunchBrandPack; error: null } | { pack: null; error: string } {
        const companyId = this.companyId.trim();
        if (!companyId) return { pack: null, error: 'Company ID is required.' };

        const emailAccountIds = this.emailAccountIdsCsv
            .split(',')
            .map((s) => Number(s.trim()))
            .filter((n) => Number.isInteger(n) && n > 0);
        if (emailAccountIds.length === 0) {
            return { pack: null, error: 'At least one valid email account ID is required.' };
        }

        if (!this.defaultScheduleId || this.defaultScheduleId <= 0) {
            return { pack: null, error: 'A valid default schedule ID is required.' };
        }

        const authorizedUsers = this.parseAuthorizedUsers();
        if (authorizedUsers.length === 0) {
            return { pack: null, error: 'Provide at least one authorized user (email:role).' };
        }
        if (!authorizedUsers.some((u) => u.role === 'Admin')) {
            return { pack: null, error: 'At least one authorized user must be an Admin.' };
        }

        const personas = this.parsePersonas();
        if (personas.length === 0) {
            return { pack: null, error: 'Provide at least one persona (name | criteria | apolloListName).' };
        }

        const sequences: LaunchBrandPack['sequences'] = [];
        const docs = this.copyDocsText.split(/^===$/m).map((d) => d.trim()).filter((d) => d.length > 0);
        if (docs.length !== personas.length) {
            return {
                pack: null,
                error: `Provide one copy doc per persona (${personas.length} expected, ${docs.length} found), separated by a line "===".`,
            };
        }
        personas.forEach((p, i) => sequences.push({ personaName: p.name, copyDocument: docs[i] }));

        const pack: LaunchBrandPack = {
            companyId,
            emailAccountIds,
            defaultScheduleId: this.defaultScheduleId,
            authorizedUsers,
            personas,
            sequences,
        };
        if (this.brandSlug.trim().length > 0) pack.brandSlug = this.brandSlug.trim();
        return { pack, error: null };
    }

    private parseAuthorizedUsers(): LaunchAuthorizedUser[] {
        const out: LaunchAuthorizedUser[] = [];
        for (const line of this.authorizedUsersText.split('\n')) {
            const trimmed = line.trim();
            if (trimmed.length === 0) continue;
            const [email, roleRaw] = trimmed.split(':').map((s) => s.trim());
            const role = roleRaw === 'Admin' || roleRaw === 'Operator' || roleRaw === 'Viewer' ? roleRaw : 'Operator';
            if (email && email.includes('@')) out.push({ userEmail: email, role });
        }
        return out;
    }

    private parsePersonas(): LaunchPersona[] {
        const out: LaunchPersona[] = [];
        for (const line of this.personasText.split('\n')) {
            const trimmed = line.trim();
            if (trimmed.length === 0) continue;
            const [name, criteria, apolloListName] = trimmed.split('|').map((s) => s.trim());
            if (name && criteria && apolloListName) {
                out.push({ name, criteria, apolloListName });
            }
        }
        return out;
    }
}
